// ANX-059 — мост между panel-кодом и Tauri 2 desktop shell.
//
// Когда panel загружается внутри Tauri webview, Tauri пробрасывает IPC.
// ВАЖНО (tauri#5088): панель грузится с REMOTE-URL (app.anexica.com), а на
// remote `withGlobalTauri` НЕ инжектит высокоуровневый `window.__TAURI__` —
// доступен только низкоуровневый `window.__TAURI_INTERNALS__.invoke`.
// Поэтому детект и invoke идут через первый доступный из двух глобалов,
// иначе на desktop всё молча no-op'ило (поллер уведомлений не стартовал).
// Каждая функция здесь — no-op в браузере и реальный invoke в desktop-shell.

import type { DeepLink } from './desktop-deep-link';

type InvokeFn = <T = unknown>(cmd: string, args?: Record<string, unknown>) => Promise<T>;
type Unlisten = () => void;
type ListenFn = <T = unknown>(
  event: string,
  handler: (event: { payload: T }) => void,
) => Promise<Unlisten>;

interface TauriGlobal {
  core: { invoke: InvokeFn };
  event: { listen: ListenFn };
}

// Низкоуровневый IPC, который Tauri 2 инжектит в т.ч. на remote-URL.
interface TauriInternals {
  invoke: InvokeFn;
}

declare global {
  interface Window {
    __TAURI__?: TauriGlobal;
    __TAURI_INTERNALS__?: TauriInternals;
  }
}

export function isDesktop(): boolean {
  return typeof window !== 'undefined' && !!(window.__TAURI__ ?? window.__TAURI_INTERNALS__);
}

// invoke, работающий и на remote-URL: предпочитаем высокоуровневый
// __TAURI__.core.invoke (локальный контент / withGlobalTauri), иначе
// низкоуровневый __TAURI_INTERNALS__.invoke (remote-origin, наш случай).
// Возвращает null, если ни одного IPC нет (обычный браузер) — caller'ы
// трактуют это как no-op.
function invoke<T = unknown>(cmd: string, args?: Record<string, unknown>): Promise<T> | null {
  if (typeof window === 'undefined') return null;
  const hi = window.__TAURI__?.core?.invoke;
  if (hi) return hi<T>(cmd, args);
  const lo = window.__TAURI_INTERNALS__?.invoke;
  if (lo) return lo<T>(cmd, args);
  return null;
}

export async function setUnreadBadge(count: number): Promise<void> {
  await invoke('set_unread_badge', { count });
}

export async function notify(title: string, body: string): Promise<void> {
  await invoke('notify_user', { title, body });
}

export async function isAutostartEnabled(): Promise<boolean> {
  return (await invoke<boolean>('is_autostart_enabled')) ?? false;
}

export async function enableAutostart(): Promise<void> {
  await invoke('enable_autostart');
}

export async function disableAutostart(): Promise<void> {
  await invoke('disable_autostart');
}

/**
 * ANX-092: check-only — версия доступного обновления или null.
 * Никакой установки: прежняя единая команда (`check_for_updates_and_install`)
 * тихо ставила + перезапускала приложение на старте, что его валило.
 * На старом бинаре (без команды `check_for_updates`) invoke реджектится —
 * глотаем в null: баннер просто не покажется.
 */
export async function checkForUpdates(): Promise<string | null> {
  try {
    return (await invoke<string | null>('check_for_updates')) ?? null;
  } catch {
    return null;
  }
}

/** ANX-092: установка обновления — только по явному клику пользователя. */
export async function installUpdate(): Promise<void> {
  await invoke('install_update');
}

export interface PollerLabels {
  /** Заголовок native-нотификации. */
  notification_title: string;
  /** Подпись по умолчанию, если у visitor нет ни имени, ни email. */
  fallback_visitor: string;
}

export async function setAuthToken(token: string, labels: PollerLabels): Promise<void> {
  await invoke('set_auth_token', { token, labels });
}

export async function clearAuthToken(): Promise<void> {
  await invoke('clear_auth_token');
}

export interface DeepLinkPayload {
  raw: string;
  parsed: DeepLink | null;
}

export async function onDeepLink(
  handler: (payload: DeepLinkPayload) => void,
): Promise<Unlisten> {
  // event.listen есть только в высокоуровневом __TAURI__ (локальный контент).
  // На remote-URL его нет — deep-link отдельная фича, для уведомлений не нужен;
  // best-effort, no-op если listen недоступен.
  const listen = typeof window !== 'undefined' ? window.__TAURI__?.event?.listen : undefined;
  if (!listen) return () => {};
  return await listen<DeepLinkPayload>('deep-link', (event) => handler(event.payload));
}
